import { useState } from 'react';
import type { ReactNode, VoidFunctionComponent } from 'react';

import * as _ from 'lodash';
import { useAsyncStatus, useOnMount } from '../../hooks';
import * as api from '../../redux/api';
import Loader from '../generic/Loader';
import type { FailedFetchResponse } from '../../helpers/errors';
import { Link } from '../generic';

type ContribCountResponse = {
  snapshot: string;
  activeContributorCountLast90days: number;
  activeContributorCountLast365days: number;
};

const ContributorsCard: VoidFunctionComponent = () => {
  const [trackedContribCount90, setContribCount90] = useState<number | null>(null);
  const [trackedContribCount365, setContribCount365] = useState<number | null>(null);
  const [trackedContribCountSnapshotDate, setContribCountDate] = useState('');
  const { status, onError, onPending, onSuccess } = useAsyncStatus();

  useOnMount(async () => {
    onPending();
    try {
      const res = await api.get<ContribCountResponse>('/api/contributors/latest-snapshot-count');
      if (res.data) {
        setContribCount90(res.data.activeContributorCountLast90days);
        setContribCount365(res.data.activeContributorCountLast365days);
        setContribCountDate(res.data.snapshot);
        onSuccess();
      }
    } catch (err) {
      onError(err as FailedFetchResponse);
    }
  });

  let content: ReactNode;
  if (status.loading) {
    content = <Loader />;
  } else if (!trackedContribCountSnapshotDate) {
    content = (
      <div className="flex-table text-center margin-top-lg">
        No snapshot data available.
      </div>
    );
  } else {
    content = (
      <div className="flex-table margin-top-xl margin-bottom-xl">
        <p className="italic centeralign margin-bottom-xl">Note: Snapshots are generated every Sunday morning.</p>
        <div className="flex-table--row">
          <div className="flex-table--row--cell"><b>Last snapshot</b></div>
          <div className="flex-table--row--cell">{trackedContribCountSnapshotDate || 'n/a'}</div>
        </div>
        <div className="flex-table--row">
          <div className="flex-table--row--cell"><b>Count (Last 90 days)</b></div>
          <div className="flex-table--row--cell">{_.isNumber(trackedContribCount90) ? trackedContribCount90 : 'n/a'}</div>
        </div>
        <div className="flex-table--row">
          <div className="flex-table--row--cell"><b>Count (Last 365 days)</b></div>
          <div className="flex-table--row--cell">{_.isNumber(trackedContribCount365) ? trackedContribCount365 : 'n/a'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="panel">
      <h2 className="text-center">Tracked Contributors</h2>
      {content}
      <div className="margin-top-lg margin-bottom-lg centeralign">
        <Link to="/api/contributors/count" openInNewTab>
          Click here to get latest (as of now) contributor count for the last 365 days
        </Link>
      </div>
    </div>
  );
};

export default ContributorsCard;
